import React, { useMemo, useState } from "react";
import { IconButton } from "@mui/material";
import { Field, FieldArray, formValueSelector } from "redux-form";
import { useSelector } from "react-redux";

import { customInput } from "../../../../../../../../common/custom-input";
import { customTextarea } from "../../../../../../../../common/customTextarea";
import AttachmentMediaUploadBoxField from "../../../../../../../SwmsMasters/EmergencyProcedures/sections/media/AttachmentMediaUploadBoxField";
import EmergencyProcedureMediaPreviewModal from "../../../../../../../SwmsMasters/EmergencyProcedures/sections/media/EmergencyProcedureMediaPreviewModal";
import { getStorage } from "../../../../../../../../../utils/common";
import { ADMIN_DETAILS } from "../../../../../../../../../dataProvider/constant";
import styles from "../../../../../../../SwmsMasters/SwmsMastersStyle.module.css";
import layoutStyles from "../../../../../../../SwmsMasters/EmergencyProcedures/EmergencyProceduresLayout.module.css";

const UPLOAD_PATH = "swms/masters/emergency-procedures";
const FORM_NAME = "JobSwmsV3EmergencyProceduresContainerForm";
const selector = formValueSelector(FORM_NAME);

const getOrgFileBasePath = () => {
  try {
    const adminDetails = JSON.parse(getStorage(ADMIN_DETAILS));
    const organisation = adminDetails && adminDetails.organisation;
    return organisation && organisation.logo_path && organisation.slug
      ? `${new URL(organisation.logo_path).origin}/${organisation.slug}/`
      : "";
  } catch (error) {
    return "";
  }
};

const resolveFileUrl = (fileValue = {}) => {
  if (!fileValue || typeof fileValue !== "object") return "";
  if (fileValue.file_url && /^https?:\/\//i.test(fileValue.file_url)) return fileValue.file_url;
  if (fileValue.url && /^https?:\/\//i.test(fileValue.url)) return fileValue.url;
  const basePath = fileValue.file_base_path || getOrgFileBasePath();
  const path = fileValue.file_url || fileValue.file_name || "";
  return path ? (/^https?:\/\//i.test(path) ? path : `${basePath}${path}`) : "";
};

const isPdfFileValue = (fileValue = {}) => {
  const type = `${fileValue.type || fileValue.file_type || ""}`.toLowerCase();
  const name = `${fileValue.visible_name || fileValue.file_name || ""}`.toLowerCase();
  return type.includes("pdf") || name.split(".").pop() === "pdf";
};

const renderAttachmentRows = ({ fields, loading, onOpenPreview }) => (
  <div>
    {fields.length === 0 ? (
      <div className={layoutStyles.MediaEmptyState}>No photos or attachments yet.</div>
    ) : null}

    {fields.map((member, index) => (
      <div key={member} className={layoutStyles.MediaAttachmentCard}>
        <div className={layoutStyles.MediaAttachmentRow}>
          <div className={layoutStyles.MediaRecordChip}>
            {index + 1}
          </div>
          <div className={layoutStyles.MediaUploadColumn}>
            <Field
              name={`${member}.file_name`}
              component={AttachmentMediaUploadBoxField}
              disabled={loading}
              s3UploadPath={UPLOAD_PATH}
              boxWidth={160}
              boxHeight={120}
              uploadLabel="Upload / Attach"
              onPreview={() => onOpenPreview(index)}
            />
          </div>
          <div className={layoutStyles.MediaFieldsColumn}>
            <Field
              name={`${member}.title`}
              component={customInput}
              className={styles.TextField}
              disabled={loading}
              placeholder="Caption"
              autoComplete="off"
            />
            <div className={layoutStyles.MediaDescriptionWrap}>
              <Field
                name={`${member}.note`}
                component={customTextarea}
                className={styles.TextArea}
                disabled={loading}
                placeholder="Description"
                rows={3}
                autoComplete="off"
              />
            </div>
          </div>
          <div className={layoutStyles.MediaDeleteColumn}>
            <IconButton
              className={`p-0 ${layoutStyles.IconButtonAutoHeight}`}
              onClick={() => fields.remove(index)}
              disabled={loading}
              title="Delete attachment record"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" className={`${styles.DeleteIcon} ${layoutStyles.MediaDeleteIcon}`}>
                <path fill="currentColor" d="M136.7 5.9L128 32 32 32C14.3 32 0 46.3 0 64S14.3 96 32 96l384 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-96 0-8.7-26.1C306.9-7.2 294.7-16 280.9-16L167.1-16c-13.8 0-26 8.8-30.4 21.9zM416 144L32 144 53.1 467.1C54.7 492.4 75.7 512 101 512L347 512c25.3 0 46.3-19.6 47.9-44.9L416 144z" />
              </svg>
            </IconButton>
          </div>
        </div>
      </div>
    ))}

    <button
      type="button"
      disabled={loading}
      onClick={() => fields.push({ file_name: null, title: "", note: "" })}
      className={layoutStyles.AddSectionButton}
    >
      <svg height="12px" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
        <path fill="currentColor" d="M256 80C256 62.3 241.7 48 224 48C206.3 48 192 62.3 192 80L192 192L80 192C62.3 192 48 206.3 48 224C48 241.7 62.3 256 80 256L192 256L192 368C192 385.7 206.3 400 224 400C241.7 400 256 385.7 256 368L256 256L368 256C385.7 256 400 241.7 400 224C400 206.3 385.7 192 368 192L256 192L256 80z" />
      </svg>
      Add photo / attachment
    </button>
  </div>
);

export default function JobSwmsV3EmergencyProcedureRescueConfigurationMediaSection({
  procedureIndex,
  loading
}) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [activePreviewIndex, setActivePreviewIndex] = useState(0);
  const attachments = useSelector((state) => selector(
    state,
    `procedures[${procedureIndex}].sections.rescue_configuration_media.data.attachments`
  )) || [];
  const previewItems = useMemo(() => (
    (Array.isArray(attachments) ? attachments : [])
      .map((attachment, rowIndex) => {
        const fileValue = attachment && attachment.file_name ? attachment.file_name : null;
        const fileUrl = resolveFileUrl(fileValue || {});
        return fileValue && fileUrl ? {
          rowIndex,
          fileUrl,
          isPdf: isPdfFileValue(fileValue),
          title: attachment.title || "",
          description: attachment.note || ""
        } : null;
      })
      .filter(Boolean)
  ), [attachments]);
  const openPreviewByRowIndex = (rowIndex) => {
    const targetIndex = previewItems.findIndex((item) => item.rowIndex === rowIndex);
    if (targetIndex >= 0) {
      setActivePreviewIndex(targetIndex);
      setPreviewOpen(true);
    }
  };

  return (
    <div className="container-fluid pb-2">
      <div className="row pt-2">
        <div className="col-12">
          <FieldArray
            name={`procedures[${procedureIndex}].sections.rescue_configuration_media.data.attachments`}
            component={renderAttachmentRows}
            loading={loading}
            onOpenPreview={openPreviewByRowIndex}
          />
          <EmergencyProcedureMediaPreviewModal
          isOpen={previewOpen}
          onClose={() => setPreviewOpen(false)}
          attachments={previewItems}
          currentIndex={activePreviewIndex}
          onPrevClick={() => setActivePreviewIndex((index) => (
            index <= 0 ? previewItems.length - 1 : index - 1
          ))}
          onNextClick={() => setActivePreviewIndex((index) => (
            index >= previewItems.length - 1 ? 0 : index + 1
          ))}
          />
        </div>
      </div>
    </div>
  );
}
